import { gsap } from "gsap";
import { map, smoothstep } from "~/lib/utils";

/**
 * About 頁面專用動畫函數
 * About page specific animation functions
 */

// GSAP animation for /about, /news, and /awards pages
// Convention: name the animation based on the page or component it's used in
export const atomsArrowButtonAnimate = () => {
    const target = ".ani-about-scroll-btn svg";
    const tl = gsap.timeline( {
        ease: "power2.out",
        repeat: -1,
    } );
    tl.to( target, {
        xPercent: "+=100",
        duration: 1,
        stagger: { amount: 0.35, from: "end" },
        delay: 1.5,
    } );
    return tl;
};

export const moleculesLandscapeExperienceAnimate = ( cameraProgress, gtagTracking ) => {
    const landscapesRefHeight = document.querySelector(
        ".slt-gsap-landscapes",
    )?.clientHeight;

    const width = window.innerWidth;

    const zoomTimeline = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-milestones",
            start: () => `bottom-=${ landscapesRefHeight } bottom`,
            endTrigger: ".slt-gsap-reveal-trigger",
            end: "top bottom",
            fastScrollEnd: true,
            scrub: 1,
            onEnter: gtagTracking,
            onEnterBack: gtagTracking,
        },
    } );

    // Phase 1: Zoom in (0% → 30% of scroll)
    zoomTimeline.to( cameraProgress, {
        value: 1,
        ease: "power1.inOut",
        duration: 1.2,
    } );
    zoomTimeline.to(
        ".slt-gsap-planets-wrapper",
        {
            xPercent: 12,
            yPercent: width >= 640 ? 18 : 35,
            scale: width >= 640 ? width >= 1270 ? 1 : 1.5 : 1.2,
            ease: "power1.inOut",
            duration: 1.2,
        },
        "<",
    );

    // show overlay
    zoomTimeline.fromTo(
        ".slt-gsap-planets-overlay",
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.3 },
        "-=0.3",
    );

    // Phase 2: Hold (30% → 70% of scroll) — use dummy tween
    zoomTimeline.to( {}, { duration: 8 } ); // nothing happens here visually

    // Phase 3: Zoom out (70% → 100%)
    zoomTimeline.to( cameraProgress, {
        value: 2,
        ease: "power1.inOut",
        duration: 1.2,
    } );
    zoomTimeline.to(
        ".slt-gsap-planets-wrapper",
        {
            xPercent: 0,
            yPercent: 0,
            scale: 1,
            ease: "power1.inOut",
            duration: 1.2,
        },
        "<",
    );
    // Fade out at end
    zoomTimeline.to(
        ".slt-gsap-landscapes",
        {
            autoAlpha: 0,
            ease: "power1.out",
            duration: 0.5,
        },
        "<",
    );
    // hide overlay
    zoomTimeline.to(
        ".slt-gsap-planets-overlay",
        { autoAlpha: 0, duration: 0.5 },
        "<",
    );
    // hide planet labels
    zoomTimeline.to(
        ".slt-gsap-planet-label",
        { autoAlpha: 0, duration: 0.5 },
        "<",
    );

    const tl2 = gsap.fromTo(
        ".slt-gsap-planets-wrapper",
        { yPercent: 0 },
        {
            yPercent: 40,
            duration: 0.5,
            ease: "power3.inout",
            scrollTrigger: {
                trigger: ".slt-gsap-awards",
                start: "top bottom",
                end: "bottom bottom",
                toggleActions: "play none none reverse",
            },
        },
    );
    return [ zoomTimeline, tl2 ];
};

export const organismAboutHeroAnimate = () => {
    // Hide the top content when the milestones section is in view
    const tl = gsap.fromTo(
        ".slt-gsap-about-top",
        {
            autoAlpha: 1,
        },
        {
            autoAlpha: 0,
            scrollTrigger: {
                trigger: ".slt-gsap-milestones",
                start: "center top",
                toggleActions: "play none none reverse",
            },
        },
    );
    return tl;
};

export const organismMilestonesAnimate = (
    progress,
    milestonesContainerHeight,
    starsCount,
    starsRadius,
    gtagTracking,
) => {
    const tl = gsap.to( progress, {
        value: 1,
        scrollTrigger: {
            trigger: ".slt-gsap-milestones",
            start: "top top",
            end: () => `${ milestonesContainerHeight.value }px bottom`,
            scrub: 2,
            onEnter: gtagTracking,
            onEnterBack: gtagTracking,
        },
        onUpdate: () => {
            const endProgress = smoothstep( 0.95, 0.97, progress.value );
            starsCount.value = ~~map( endProgress, 0, 1, 50, 10000 );
            starsRadius.value = map( endProgress, 0, 1, 500, 100 );
        },
    } );

    const landscapesRef = document.querySelector( ".slt-gsap-landscapes" );

    // Combined show -> hold -> hide animation
    const tlSkipButton = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-milestones",
            start: "5% top",
            end: () => {
                const h = landscapesRef?.clientHeight ?? 0;
                return `bottom-=${ h } bottom`; // fade out when nearing landscapes
            },
            scrub: true,
            toggleActions: "play none none reverse",
        },
    } );
    // Show
    tlSkipButton.fromTo(
        ".slt-gsap-skip-button",
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.1 },
    );
    // Hold
    tlSkipButton.to( ".slt-gsap-skip-button", {
        autoAlpha: 1,
        duration: 2,
    } );
    // Hide
    tlSkipButton.to( ".slt-gsap-skip-button", {
        autoAlpha: 0,
        duration: 0.1,
    } );

    return [ tl, tlSkipButton ];
};

export const organismMilestoneCanvasMonitor = ( pause, resume ) => {
    const tl = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-reveal-trigger",
            start: "top bottom",
            end: "bottom bottom",
        },
    } );
    return tl;
};

export const organismLandscapesAnimate = (
    landscapesProgress,
    landscapesLength,
    selectedLandscape,
) => {
    const landscapesRef = document.querySelector( ".slt-gsap-landscapes" );
    const landscapesTitle = landscapesRef?.querySelector(
        ".slt-gsap-landscapes-title",
    );
    const landscapesContent = landscapesRef?.querySelector(
        ".slt-gsap-landscapes-content",
    );

    const tl = gsap.timeline( {
        scrollTrigger: {
            id: "stLandscapes",
            trigger: ".slt-gsap-milestones",
            start: () => `bottom-=${ landscapesRef?.clientHeight } bottom`,
            end: "bottom bottom",
            toggleActions: "play none none reverse",
            preventOverlaps: true,
            scrub: 1,
            invalidateOnRefresh: true,
        },
    } );

    // 0. pointer-events
    tl.fromTo(
        landscapesRef,
        { pointerEvents: "none" },
        { pointerEvents: "auto" },
    );
    // 1. fade in + move up title
    tl.fromTo(
        landscapesTitle,
        { yPercent: 5, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1 },
        "<",
    );

    // 2. hold title
    tl.to( landscapesTitle, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 2,
    } );

    // 3. fade out + move title up more
    tl.to( landscapesTitle, {
        yPercent: -5,
        autoAlpha: 0,
    } );

    // 4. li fade in + move in from right
    tl.fromTo(
        landscapesContent.children[ 0 ],
        { autoAlpha: 0 },
        {
            autoAlpha: 1,
            duration: 1,
        },
    ).fromTo(
        landscapesContent.children[ 1 ],
        { autoAlpha: 0 },
        {
            autoAlpha: 1,
            duration: 1,
        },
        "<",
    );

    tl.fromTo(
        landscapesContent.children[ 2 ],
        { autoAlpha: 0, yPercent: 5 },
        {
            autoAlpha: 1,
            yPercent: 0,
            stagger: 0.01,
        },
        "<+.2",
    );

    // 5. update landscapes progress
    tl.to( landscapesProgress, {
        value: 1,
        duration: 4,
        ease: "linear",
        onUpdate: () => {
            // Get exact position
            const exactIndex =
                landscapesProgress.value * ( landscapesLength.value - 1 );

            // Add a small delay before snapping for more natural feel
            const snapThreshold = 0.4;
            const snapStrength = 0.8; // Stronger snap (0-1)

            // Calculate distance to nearest snap point
            const nearestIndex = Math.round( exactIndex );
            const distanceToNearestIndex = Math.abs( exactIndex - nearestIndex );

            // Apply non-linear easing to the snap
            let snappedPosition;

            if ( distanceToNearestIndex < snapThreshold ) {
                const t = distanceToNearestIndex / snapThreshold;
                const snapInfluence = 1 - smoothstep( 0, 1, t );
                snappedPosition = gsap.utils.interpolate(
                    exactIndex,
                    nearestIndex,
                    snapInfluence * snapStrength,
                );
            } else {
                snappedPosition = exactIndex;
            }

            // Update the selectedLandscape value
            selectedLandscape.value = snappedPosition;
            // selectedLandscape.value = exactIndex;
        },
    } );

    return tl;
};

export const organismPlanetsAnimate = ( pause, resume ) => {
    const landscapesRef = document.querySelector( ".slt-gsap-landscapes" );

    const tl = gsap.fromTo(
        ".slt-gsap-planets-wrapper",
        {
            autoAlpha: 0,
            scale: 0.6,
        },
        {
            autoAlpha: 1,
            scale: 1,
            duration: 0.3,
            scrollTrigger: {
                trigger: ".slt-gsap-milestones",
                start: () =>
                    `bottom-=${ 1.2 * landscapesRef?.clientHeight } bottom`,
                end: `bottom-=${ landscapesRef?.clientHeight }  top`,
                toggleActions: "play none none reverse",
                onEnter: resume,
                onLeaveBack: pause,
            },
        },
    );
    return tl;
};

export const organismAboutSloganAnimate = ( setHeaderMode ) => {
    const revealSection = document.querySelector( ".slt-gsap-reveal" );

    const tl = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-reveal-trigger", // Element that triggers the animation
            start: "top bottom", // Start when the top of revealSection hits the bottom of the viewport
            end: "bottom bottom", // End when the top of revealSection hits the top of the viewport
            scrub: 0.8,
            fastScrollEnd: true,
            onEnter: () => setHeaderMode( "light" ),
            onLeave: () => setHeaderMode( "light" ),
            onLeaveBack: () => setHeaderMode( "dark" ),
        },
    } );
    // Animate the revealSection
    tl.fromTo(
        revealSection,
        { clipPath: "inset(100% 0% 0)" }, // Start position: 100% down (off-screen below)
        {
            clipPath: "inset(0% 0 0)", // End position: 0% (fully covering the viewport)
            duration: 6,
        },
    )
        .fromTo(
            ".slt-gsap-milestones > div",
            {
                display: "block",
            },
            {
                display: "none",
            },
        )
        .fromTo(
            revealSection.querySelector( "svg" ),
            {
                scale: 1,
                rotate: 0,
            },
            { scale: 5, rotate: 360, duration: 8 },
        )
        .fromTo(
            ".slt-gsap-planets-wrapper",
            { scale: 1 },
            { scale: 0.6, duration: 8, ease: "power3.inout" },
            "<",
        )
        .to( revealSection, {
            clipPath: "inset(0 0 100% 0)", // End position: 0% (fully covering the viewport)
            duration: 2,
        } );
    return tl;
};

export const organismAwardsAnimate = ( callback, gtagTracking ) => {
    const tl = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-awards",
            start: "top bottom",
            onEnter: () => {
                callback();
                gtagTracking();
            },
            onEnterBack: gtagTracking,
            onLeaveBack: callback
        },
    } );
    return tl;
};

export const organismTeamsAnimate = ( callback, gtagTracking ) => {
    const tl = gsap.timeline( {
        scrollTrigger: {
            trigger: ".slt-gsap-teams",
            start: "top bottom",
            onEnter: () => {
                callback();
                gtagTracking();
            },
            onEnterBack: gtagTracking,
            onLeaveBack: callback,
        },
    } );
    return tl;
};
